import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { isApiError, isValidationError } from '@/services/api';
import { useQuote } from '@features/quote/context/QuoteContext';
import type { ScheduleOverrides } from '@features/quote/types/quote.types';
import { todayDateKey, type DateKey } from '@features/quote/utils/dateKey';
import type { VehicleScheduleContext } from '@/app/utils/vehicleScheduleContext';
import { fetchDerivedSchedule } from '../services/derivedSchedule';
import type { DerivedScheduleServiceEntry } from '../types/derivedSchedule.types';
import { verifyScheduleOverrides, vetOverrideDatesForRequest } from '../utils/scheduleOverridesGuard';
import type { DerivationContext } from '../utils/derivationFingerprint';
import { unhonouredOverrides } from '../utils/unhonouredOverrides';
import { scheduleOverridesKey } from '../utils/scheduleOverridesKey';
import { SCHEDULE_OVERRIDE_MESSAGES } from '../utils/scheduleOverrideMessages';

// Distinguishes a refused/mismatched edit from a genuine transport failure
// in the setRowDate catch block, without string-matching an Error message.
class ScheduleEditRefusedError extends Error {}

// Mirrors useDerivedSchedule's vehicle mapping; duplicated because this
// materialisation call is never routed through that query (design 3.2).
function vehicleRequestParams(vehicleContext: VehicleScheduleContext) {
    return {
        vehicleAgeYears: vehicleContext.vehicleAgeYears,
        firstRegistrationDate: vehicleContext.firstRegistrationDate,
        vehicleMileage: vehicleContext.vehicleMileage,
        annualMileage: vehicleContext.annualMileage,
        lastServiceDate: vehicleContext.lastServiceDate,
        lastServiceMileage: vehicleContext.lastServiceMileage,
        makeId: vehicleContext.makeId,
        modelId: vehicleContext.modelId,
        derivativeId: vehicleContext.derivativeId,
        transmissionId: vehicleContext.transmissionId,
        fuelTypeId: vehicleContext.fuelTypeId,
        engineSize: vehicleContext.engineSize,
        is4x4: vehicleContext.is4x4,
    };
}

// A cascade re-derives the anchor and every row after it, so any date an
// earlier edit pinned there would otherwise be re-applied over the re-derivation.
function overridesBeforeRow(
    dateOverrides: Record<number, DateKey>,
    rowIndex: number
): Record<number, DateKey> {
    return Object.fromEntries(
        Object.entries(dateOverrides).filter(([row]) => Number(row) < rowIndex)
    ) as Record<number, DateKey>;
}

export interface UseScheduleOverridesArgs {
    // The same value `useDerivedServices` returns, computed from its
    // ref-hardened resolved plan id.
    fingerprint: string;
    // The product and vehicle the request is about to be built from, known a
    // render before the resolved plan id the fingerprint above waits on.
    derivationContext: DerivationContext;
    // The rows actually in use (the guided-visible slice, not the full
    // authoritative list) — the same rows `unhonouredOverrides` is passed.
    derivedRows: DerivedScheduleServiceEntry[];
    // True once the derive query is settled (not merely non-loading), so the
    // checks below never run against an in-flight or stale result.
    isDerivedSettled: boolean;
    derivedError: unknown;
    // Whether the request behind `derivedRows` actually carried the dates. A
    // withheld map cannot be read as drift without discarding a valid document.
    dateOverridesInRequest: boolean;
    // The same `count` the authoritative `useDerivedSchedule` query sends, so
    // a cascade materialises exactly as many rows as the panel can show.
    requestedCount: number | undefined;
}

// A superseded cascade is a silent no-op, not a refusal: the caller must not
// treat it as an applied edit, and must not surface an error for it.
export type SetRowDateResult = { status: 'applied' } | { status: 'superseded' };

export interface UseScheduleOverridesResult {
    overrides: ScheduleOverrides | undefined;
    startEntryIndex: number | null;
    dateOverrides: Record<number, DateKey>;
    setStartEntryIndex: (index: number) => void;
    setRowDate: (rowIndex: number, date: DateKey, cascade: boolean) => Promise<SetRowDateResult>;
    isMaterialising: boolean;
    materialiseError: string | null;
    clearMaterialiseError: () => void;
}

// Orchestrates the dealer's start-service/date-override state: stale-document
// invalidation, the 422 safety net, and the unhonoured-drift check.
export function useScheduleOverrides({
    fingerprint,
    derivationContext,
    derivedRows,
    isDerivedSettled,
    derivedError,
    dateOverridesInRequest,
    requestedCount,
}: UseScheduleOverridesArgs): UseScheduleOverridesResult {
    const { scheduleOverrides, setScheduleOverrides, clearPaymentSelection } = useQuote();
    const [isMaterialising, setIsMaterialising] = useState(false);
    const [materialiseError, setMaterialiseError] = useState<string | null>(null);

    // Read fresh every render, not memoised, so a day rollover is picked up
    // on the next render rather than staying pinned to the mount-time value.
    const today = todayDateKey();

    // Staleness, unreadability, and expiry collapse into one verdict, memoised
    // so both effects below re-run only when their real inputs change.
    const verdict = useMemo(
        () =>
            scheduleOverrides
                ? verifyScheduleOverrides(scheduleOverrides, fingerprint, today)
                : { usable: true as const },
        [scheduleOverrides, fingerprint, today]
    );

    // Waits for the derive to settle so an in-flight window's still-unknown
    // fingerprint is never read as a genuine staleness mismatch.
    useEffect(() => {
        if (!isDerivedSettled || !scheduleOverrides || verdict.usable) return;
        clearPaymentSelection();
        if (verdict.scope === 'document') {
            setScheduleOverrides(undefined);
            toast.error(SCHEDULE_OVERRIDE_MESSAGES[verdict.reason], { position: 'bottom-right' });
        } else {
            setScheduleOverrides({ ...scheduleOverrides, dateOverrides: {} });
            toast.error(SCHEDULE_OVERRIDE_MESSAGES.expired, { position: 'bottom-right' });
        }
    }, [isDerivedSettled, scheduleOverrides, verdict, clearPaymentSelection, setScheduleOverrides]);

    // Terminates by construction: the discard empties dateOverrides, so the
    // retry it triggers finds nothing left to refuse or mismatch.
    useEffect(() => {
        if (!isDerivedSettled || !scheduleOverrides || !verdict.usable) return;
        if (!dateOverridesInRequest) return;

        const refused = isValidationError(derivedError);
        const mismatched = refused ? [] : unhonouredOverrides(scheduleOverrides.dateOverrides, derivedRows);
        if (!refused && mismatched.length === 0) return;

        clearPaymentSelection();
        setScheduleOverrides({ ...scheduleOverrides, dateOverrides: {} });
        toast.error(SCHEDULE_OVERRIDE_MESSAGES.unhonoured, { position: 'bottom-right' });
    }, [
        isDerivedSettled,
        scheduleOverrides,
        verdict,
        derivedError,
        derivedRows,
        dateOverridesInRequest,
        clearPaymentSelection,
        setScheduleOverrides,
    ]);

    // Every request-driving input, not just the fingerprint's: matcher-only
    // vehicle fields and count change the response without moving the fingerprint.
    const materialiseContextKey = JSON.stringify([
        fingerprint,
        requestedCount ?? null,
        derivationContext.vehicleContext.vehicleAgeYears ?? null,
        derivationContext.vehicleContext.vehicleFuelType ?? null,
        derivationContext.vehicleContext.makeId ?? null,
        derivationContext.vehicleContext.modelId ?? null,
        derivationContext.vehicleContext.derivativeId ?? null,
        derivationContext.vehicleContext.transmissionId ?? null,
        derivationContext.vehicleContext.fuelTypeId ?? null,
        derivationContext.vehicleContext.engineSize ?? null,
        derivationContext.vehicleContext.is4x4 ?? null,
    ]);

    // fetchDerivedSchedule runs outside React Query, so a completion that lands
    // after an unmount or a superseding change must write nothing (F2).
    const materialiseGeneration = useRef(0);
    useEffect(
        () => () => {
            materialiseGeneration.current += 1;
            setIsMaterialising(false);
            setMaterialiseError(null);
        },
        [materialiseContextKey]
    );

    // The mutable request inputs the context key cannot see: a foreign write to
    // either supersedes an in-flight cascade without starting another one.
    const requestIdentity = `${scheduleOverrides?.startEntryIndex ?? null}|${scheduleOverridesKey(
        scheduleOverrides?.dateOverrides
    )}`;
    const requestIdentityRef = useRef(requestIdentity);
    useEffect(() => {
        requestIdentityRef.current = requestIdentity;
    }, [requestIdentity]);

    const setStartEntryIndex = useCallback(
        (index: number): void => {
            setScheduleOverrides({
                startEntryIndex: index,
                dateOverrides: scheduleOverrides?.dateOverrides ?? {},
                derivationFingerprint: fingerprint,
            });
            clearPaymentSelection();
        },
        [scheduleOverrides, fingerprint, setScheduleOverrides, clearPaymentSelection]
    );

    // Uncascaded: one local write, no network call. Cascaded: the direct
    // materialisation call, confirmed against its own response before storing.
    const setRowDate = useCallback(
        async (rowIndex: number, date: DateKey, cascade: boolean): Promise<SetRowDateResult> => {
            setMaterialiseError(null);
            const baseDateOverrides = scheduleOverrides?.dateOverrides ?? {};
            const currentStartEntryIndex = scheduleOverrides?.startEntryIndex ?? null;

            if (!cascade) {
                setScheduleOverrides({
                    startEntryIndex: currentStartEntryIndex,
                    dateOverrides: { ...baseDateOverrides, [rowIndex]: date },
                    derivationFingerprint: fingerprint,
                });
                clearPaymentSelection();
                return { status: 'applied' };
            }

            if (!derivationContext.productId) {
                setMaterialiseError(SCHEDULE_OVERRIDE_MESSAGES.refusedEdit);
                throw new ScheduleEditRefusedError();
            }

            const preservedOverrides = overridesBeforeRow(baseDateOverrides, rowIndex);
            setIsMaterialising(true);
            const generation = ++materialiseGeneration.current;
            const identity = requestIdentity;
            const isSuperseded = (): boolean =>
                generation !== materialiseGeneration.current ||
                identity !== requestIdentityRef.current;
            try {
                const response = await fetchDerivedSchedule(derivationContext.productId, {
                    ...vehicleRequestParams(derivationContext.vehicleContext),
                    count: requestedCount,
                    startEntryIndex: currentStartEntryIndex,
                    dateOverrides: { ...preservedOverrides, [rowIndex]: date },
                    cascadeFrom: rowIndex,
                });

                if (isSuperseded()) return { status: 'superseded' };

                if (response.services[rowIndex]?.dueDate !== date) {
                    setMaterialiseError(SCHEDULE_OVERRIDE_MESSAGES.refusedEdit);
                    throw new ScheduleEditRefusedError();
                }

                const materialised: Record<number, DateKey> = { ...preservedOverrides };
                for (let row = rowIndex; row < response.services.length; row++) {
                    materialised[row] = response.services[row].dueDate;
                }
                setScheduleOverrides({
                    startEntryIndex: currentStartEntryIndex,
                    dateOverrides: materialised,
                    derivationFingerprint: fingerprint,
                });
                clearPaymentSelection();
                return { status: 'applied' };
            } catch (err) {
                if (isSuperseded()) return { status: 'superseded' };
                if (isApiError(err)) {
                    setMaterialiseError(err.message || SCHEDULE_OVERRIDE_MESSAGES.refusedEdit);
                } else if (!(err instanceof ScheduleEditRefusedError)) {
                    setMaterialiseError(SCHEDULE_OVERRIDE_MESSAGES.refusedEdit);
                }
                throw err;
            } finally {
                if (generation === materialiseGeneration.current) {
                    setIsMaterialising(false);
                }
            }
        },
        [
            scheduleOverrides,
            fingerprint,
            derivationContext,
            requestedCount,
            requestIdentity,
            setScheduleOverrides,
            clearPaymentSelection,
        ]
    );

    const clearMaterialiseError = useCallback(() => setMaterialiseError(null), []);

    return {
        overrides: scheduleOverrides,
        startEntryIndex: scheduleOverrides?.startEntryIndex ?? null,
        dateOverrides: vetOverrideDatesForRequest(scheduleOverrides, today, derivationContext) ?? {},
        setStartEntryIndex,
        setRowDate,
        isMaterialising,
        materialiseError,
        clearMaterialiseError,
    };
}
